import clsx from 'clsx'
import { useState } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { formatMoney } from 'helpers/utils'

const UPSELL_IMAGE_WIDTHS = [160, 240, 320, 480]

const getOptionValue = (variant, matcher) => {
  const opts = variant?.options
  if (opts && typeof opts === 'object' && !Array.isArray(opts)) {
    for (const [key, val] of Object.entries(opts)) {
      if (matcher(key)) {
        return val && typeof val === 'object' ? val.value ?? null : val
      }
    }
  }
  return null
}

const getDisplaySize = (variant) => {
  const optionValue =
    getOptionValue(variant, (key) => /size|value/i.test(key)) ??
    variant?.size ??
    variant?.Size ??
    getOptionValue(variant, (key) => !/colou?r/i.test(key))

  const label =
    optionValue ??
    variant?.option2 ??
    variant?.option1 ??
    variant?.title ??
    variant?.name

  return label != null && String(label).trim() !== '' ? String(label).trim() : 'N/A'
}

const isAvailable = (variant) => variant?.available !== false && variant?.available !== 'false'

/**
 * Price display used when a caller supplies `options.priceOverride` (e.g. GWP
 * discounted / free gifts). Values are in cents. Mirrors the sale styling used
 * elsewhere: "Free" for a zero price, brand-sale colour otherwise, with the
 * original price struck through when a discount is applied.
 */
function OverridePrice({ price, compareAtPrice, className }) {
  const isFree = !price || price <= 0
  const showCompare = compareAtPrice != null && price < compareAtPrice
  return (
    <p className={clsx('shrink-0 flex items-center gap-0.25', className)}>
      <span className={clsx('paragraph-xs uppercase font-medium', isFree ? 'text-brand-black' : 'text-brand-sale')}>
        {isFree ? 'Free' : formatMoney(price)}
      </span>
      {showCompare && (
        <span className="line-through paragraph-xs text-brand-black">{formatMoney(compareAtPrice)}</span>
      )}
    </p>
  )
}

function SizeSelect({ variants, selectedVariant, onSelect, optionName }) {
  const selectedLabel = selectedVariant ? getDisplaySize(selectedVariant) : 'Select'
  return (
    <label className="relative flex items-center gap-x-0.5 w-full h-[36px] px-0.625 border border-neutral-medium cursor-pointer link-fade">
      <span className="flex items-center gap-x-0.5 justify-between w-full paragraph-s leading-none whitespace-nowrap pointer-events-none">
        <span>{selectedLabel}</span>
        <span className="shrink-0 w-[13px] h-[7px] children:size-full">
          <Icon id="chevron-down" />
        </span>
      </span>
      <select
        className="absolute inset-0 size-full !pr-2 opacity-0 border-0 bg-transparent appearance-none cursor-pointer focus:outline-none focus:ring-0 focus:ring-offset-0 z-2"
        value={selectedVariant ? String(selectedVariant.id) : ''}
        onChange={(event) => {
          const { value } = event.currentTarget
          onSelect(variants.find((variant) => String(variant.id) === value) || null)
        }}
        aria-label={`Select ${optionName}`}
      >
        <option value="" disabled>
          {optionName}
        </option>
        {variants.map((variant) => {
          const available = isAvailable(variant)
          return (
            <option key={variant.id} value={variant.id} disabled={!available}>
              {getDisplaySize(variant)}
              {available ? '' : ' — Sold Out'}
            </option>
          )
        })}
      </select>
    </label>
  )
}

export function ResultUpsellCard({ result, className, dataIndex = '0', options = {} }) {
  const atcText = options.atcText || 'Add To Bag'
  const soldOutText = options.soldOutText || 'Sold Out'

  const variants = Array.isArray(result.variants) ? result.variants : []

  const isGiftCard = result.is_giftcard === 'true' || result.template_suffix === 'gift-card'
  const optionName = isGiftCard ? 'Value' : 'Size'

  const productName = result.mfield_utopia_customer_facing_product_name?.trim()
    ? result.mfield_utopia_customer_facing_product_name
    : result.name

  const url = `/products/${result.handle}`
  const productId = result.parentId ?? result.groupId ?? result.uid

  const hasSingleVariant = variants.length <= 1
  const singleVariant = variants[0]
  const anyAvailable = variants.some(isAvailable)

  const [selectedVariant, setSelectedVariant] = useState(null)
  const [loading, setLoading] = useState(false)

  const clickLogger = useBeaconLogClick()

  const handleLogClick = () => {
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(result)
    clickLogger(result)
  }

  const addToCart = async (variant) => {
    if (!variant) return

    setLoading(true)
    try {
      if (options.onAddToCart) {
        await options.onAddToCart(variant)
      } else if (window.Cart) {
        const variantId = typeof variant.id === 'string' ? parseInt(variant.id, 10) : variant.id
        await window.Cart.addLineItems({ id: variantId, quantity: 1 })
      }
    } catch (error) {
      console.error('Error adding upsell item to cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const buttonClass = 'shrink-0 btn btn-s'

  const renderAtc = () => {
    if (!anyAvailable) {
      return (
        <div className="flex w-full">
          <button className={clsx(buttonClass, 'btn-primary btn-fill w-full lg:w-fit')} aria-label={soldOutText} disabled>
            <span className="!uppercase">{soldOutText}</span>
          </button>
        </div>
      )
    }

    if (hasSingleVariant) {
      return (
        <div className="flex w-full">
          <button
            type="button"
            className={clsx(buttonClass, 'btn-primary btn-fill w-full lg:w-fit', loading && 'disabled')}
            onClick={() => addToCart(singleVariant)}
            disabled={loading}
            aria-label={atcText}
          >
            <span className="!uppercase">{atcText}</span>
          </button>
        </div>
      )
    }

    return (
      <div className="flex lg:grid lg:grid-cols-2 lg:items-center lg:justify-between gap-x-0.5 gap-y-0.125">
        <SizeSelect
          variants={variants}
          selectedVariant={selectedVariant}
          onSelect={setSelectedVariant}
          optionName={optionName}
        />

        <div className="shrink-0 flex">
          {selectedVariant ? (
            <button
              type="button"
              className={clsx(buttonClass, 'btn-primary btn-fill w-full', loading && 'disabled')}
              onClick={() => addToCart(selectedVariant)}
              disabled={loading}
              aria-label={atcText}
            >
              <span className="!uppercase">{atcText}</span>
            </button>
          ) : (
            <button className={clsx(buttonClass, 'btn-primary btn-fill w-full pointer-events-none')} aria-label="Select">
              <span className="!uppercase">Select</span>
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx('grow flex items-stretch border border-neutral-medium', className)}
      data-dl-track="select_item"
      data-dl-product-id={productId}
      data-dl-index={dataIndex}
    >
      <a href={url} onClick={handleLogClick} className="shrink-0 w-[86px] aspect-2/3" aria-label={result.name}>
        <ResultImage result={result} sizes="(min-width: 768px) 178px, 50vw" widths={UPSELL_IMAGE_WIDTHS} />
      </a>

      <div className="grow flex flex-col justify-between gap-y-0.75 min-w-0 h-auto p-1">
        <div className="flex flex-col gap-y-0.25 w-full min-w-0">
          <a href={url} className="paragraph-s text-brand-black font-serif link-fade line-clamp-1" aria-label={result.name} dangerouslySetInnerHTML={{ __html: productName }} />

          {options.priceOverride ? (
            <OverridePrice
              price={options.priceOverride.price}
              compareAtPrice={options.priceOverride.compareAtPrice}
            />
          ) : (
            <Price
              price={String(result.price ?? '')}
              compareAtPrice={result.compare_at_price != null ? String(result.compare_at_price) : undefined}
              className="flex items-center gap-0.5"
            />
          )}
        </div>

        {renderAtc()}
      </div>
    </div>
  )
}
